import React from 'react';
import { Modal as MModal, ModalBody, ModalContent } from "@heroui/react";

import { store } from '@/app/store';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';

interface ChangePasswordProps {
  onClose: () => void;
}

// Mirrors the server's complexity policy (spec lines 50-60). Evaluated live
// so the user sees each rule tick green as they type.
const rules = [
  { label: '8–15 characters',          test: (p: string) => p.length >= 8 && p.length <= 15 },
  { label: 'An uppercase letter',      test: (p: string) => /[A-Z]/.test(p) },
  { label: 'A lowercase letter',       test: (p: string) => /[a-z]/.test(p) },
  { label: 'A digit',                  test: (p: string) => /\d/.test(p) },
  { label: 'A special character',      test: (p: string) => /[^A-Za-z0-9\s]/.test(p) },
];

// Cryptographically-strong random int in [0, max). Math.random is unsafe
// for password generation (predictable across tabs / sessions).
function randomInt(max: number): number {
  const buf = new Uint32Array(1);
  crypto.getRandomValues(buf);
  return buf[0] % max;
}

// Produces a 12-char password guaranteed to satisfy every server rule:
// one upper, one lower, one digit, one special, then 8 more random chars
// from the combined set, then shuffled so the "guaranteed" chars don't sit
// in the same position every time.
function generatePassword(): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%^&*-_+=?';
  const all = upper + lower + digits + special;
  const must = [
    upper[randomInt(upper.length)],
    lower[randomInt(lower.length)],
    digits[randomInt(digits.length)],
    special[randomInt(special.length)],
  ];
  const len = 12;
  const rest = Array.from({ length: len - must.length }, () => all[randomInt(all.length)]);
  const out = [...must, ...rest];
  for (let i = out.length - 1; i > 0; i--) {
    const j = randomInt(i + 1);
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out.join('');
}

const ChangePassword: React.FC<ChangePasswordProps> = ({ onClose }) => {
  const [currentPwd, setCurrentPwd] = React.useState('');
  const [newPwd, setNewPwd] = React.useState('');
  const [confirmPwd, setConfirmPwd] = React.useState('');
  const [showCurrent, setShowCurrent] = React.useState(false);
  const [showNew, setShowNew] = React.useState(false);
  const [showConfirm, setShowConfirm] = React.useState(false);
  const [currentError, setCurrentError] = React.useState('');
  const [newError, setNewError] = React.useState('');
  const [confirmError, setConfirmError] = React.useState('');
  const [formError, setFormError] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);

  const ruleStates = React.useMemo(
    () => rules.map((r) => ({ label: r.label, ok: r.test(newPwd) })),
    [newPwd],
  );
  const matches = newPwd.length > 0 && newPwd === confirmPwd;
  const allRulesPass = ruleStates.every((r) => r.ok);
  const canSubmit = !!currentPwd && allRulesPass && matches && newPwd !== currentPwd && !submitting;

  const handleGenerate = () => {
    const pwd = generatePassword();
    setNewPwd(pwd);
    setConfirmPwd(pwd);
    setShowNew(true);
    setShowConfirm(true);
    setNewError('');
    setConfirmError('');
  };

  const handleSubmit = React.useCallback(async () => {
    setCurrentError('');
    setNewError('');
    setConfirmError('');
    setFormError('');

    if (!currentPwd.trim()) {
      setCurrentError('Enter your current password.');
      setFormError('Enter your current password.');
      return;
    }
    if (!allRulesPass) {
      setNewError('New password still misses one of the rules below.');
      setFormError('New password still misses one of the rules below.');
      return;
    }
    if (newPwd === currentPwd) {
      setNewError('New password must differ from current password.');
      setFormError('New password must differ from current password.');
      return;
    }
    if (newPwd !== confirmPwd) {
      setConfirmError('Passwords do not match.');
      setFormError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await store.changeOwnPassword(currentPwd, newPwd);
      if (result.ok) {
        setDone(true);
        return;
      }
      // Always surface the server's verbatim message in the banner so the
      // user sees exactly what the backend said, in addition to the inline
      // error on the relevant field.
      setFormError(result.message || 'Something went wrong.');
      switch (result.code) {
        case 4:
          setCurrentError(result.message || 'Current password is incorrect.');
          break;
        case 5:
          setNewError(result.message || 'Password does not meet complexity requirements.');
          break;
        case 6:
          setNewError(result.message || 'New password must differ from current password.');
          break;
      }
    } finally {
      setSubmitting(false);
    }
  }, [currentPwd, newPwd, confirmPwd, allRulesPass]);

  React.useEffect(() => {
    if (!done) return;
    const id = setTimeout(() => { onClose(); store.performLogout(); }, 1500);
    return () => clearTimeout(id);
  }, [done, onClose]);

  const inputCn = "px-[16px] py-[12px] rounded-[8px] border-solid border-1 border-[--Mid-Grey] !bg-white";

  const checklist = [...ruleStates, { label: 'New and confirm match', ok: matches }];

  const eye = (shown: boolean, toggle: () => void) => (
    <span className="material-icons-outlined cursor-pointer !text-[--Dark-Grey] !text-[20px]"
          onClick={toggle}>
      { shown ? 'visibility_off' : 'visibility' }
    </span>
  );

  return (
    <MModal backdrop="opaque"
            defaultOpen={true}
            hideCloseButton={true}
            scrollBehavior="outside"
            classNames={{
              backdrop: "bg-[#0000007f]",
              wrapper: "modalShadow",
              base: "!max-h-none rounded-[16px] !max-w-[520px] w-full",
              body: "!overflow-visible",
            }}
            onClose={onClose}
            size="lg"
            radius="lg">
      <ModalContent>
        <ModalBody className="w-full p-[32px] !overflow-visible">
          <FlexRow direction="column" className="gap-[16px] items-center">
            <FlexRow flex={0}
                     justify="center"
                     className="w-[80px] h-[80px] shrink-0 rounded-full bg-[#EDF1F9] items-center">
              <span className="material-icons-outlined !text-[--Primetime-main] text-[36px]">
                { done ? 'check_circle' : 'lock' }
              </span>
            </FlexRow>

            <h2 className="text-[24px] font-bold text-black m-0 text-center">
              Change password
            </h2>

            { done
              ? <>
                  <p className="text-[14px] text-[--Dark-Grey] m-0 text-center leading-[20px]">
                    Your password has been changed. You'll be signed out now — use the new password to sign back in.
                  </p>
                  <FlexRow flex={0} justify="center" className="w-full pt-[4px]">
                    <Button caption="Sign in again"
                            onClick={() => { onClose(); store.performLogout(); }} />
                  </FlexRow>
                </>
              : <>
                  <Input value={currentPwd}
                         label="Current password"
                         onValueChange={setCurrentPwd}
                         type={showCurrent ? 'text' : 'password'}
                         autoComplete="new-password"
                         name="cp-current"
                         isInvalid={!!currentError}
                         errorMessage={currentError}
                         classNames={{ inputWrapper: inputCn }}
                         endContent={eye(showCurrent, () => setShowCurrent(!showCurrent))} />

                  <Input value={newPwd}
                         label="New password"
                         onValueChange={setNewPwd}
                         type={showNew ? 'text' : 'password'}
                         autoComplete="new-password"
                         name="cp-new"
                         isInvalid={!!newError}
                         errorMessage={newError}
                         classNames={{ inputWrapper: inputCn }}
                         endContent={eye(showNew, () => setShowNew(!showNew))} />

                  <Input value={confirmPwd}
                         label="Confirm new password"
                         onValueChange={setConfirmPwd}
                         type={showConfirm ? 'text' : 'password'}
                         autoComplete="new-password"
                         name="cp-confirm"
                         isInvalid={!!confirmError}
                         errorMessage={confirmError}
                         classNames={{ inputWrapper: inputCn }}
                         endContent={eye(showConfirm, () => setShowConfirm(!showConfirm))} />

                  <FlexRow flex={0} justify="end" className="w-full">
                    <span onClick={handleGenerate}
                          className="text-[--Primetime-main] text-[13px] font-bold cursor-pointer hover:underline flex items-center gap-[6px]">
                      <span className="material-icons text-[16px] !text-[--Primetime-main]">auto_awesome</span>
                      Generate a strong password
                    </span>
                  </FlexRow>

                  {/* Live password requirement checklist */}
                  <div className="w-full border border-[--Mid-Grey] rounded-[8px] p-[16px] bg-[--Light-Grey]">
                    <div className="text-[11px] font-bold text-[--Dark-Grey] mb-[10px] uppercase tracking-wider">
                      Password must include
                    </div>
                    <div className="grid grid-cols-2 gap-x-[12px] gap-y-[8px]">
                      { checklist.map((r) => (
                        <div key={r.label} className="flex items-center gap-[8px] text-[13px]">
                          <span className={`material-icons text-[18px] ${r.ok ? '!text-[--Primetime-main]' : '!text-[--Mid-Grey]'}`}>
                            { r.ok ? 'check_circle' : 'radio_button_unchecked' }
                          </span>
                          <span className={r.ok ? 'text-black' : 'text-[--Dark-Grey]'}>
                            { r.label }
                          </span>
                        </div>
                      )) }
                    </div>
                  </div>

                  { formError &&
                    <div className="w-full bg-[#FDECEA] border border-[--Error] rounded-[8px] p-[10px] flex items-start gap-[6px]">
                      <span className="material-icons !text-[--Error] text-[16px]">error</span>
                      <span className="text-[12px] text-[--Error] leading-[16px]">{ formError }</span>
                    </div>
                  }

                  <FlexRow flex={0} justify="center" className="gap-[12px] w-full pt-[4px]">
                    <Button caption="Cancel"
                            layout="white"
                            onClick={onClose} />
                    <Button caption={submitting ? "Saving…" : "Change password"}
                            isDisabled={!canSubmit}
                            onClick={handleSubmit} />
                  </FlexRow>
                </>
            }
          </FlexRow>
        </ModalBody>
      </ModalContent>
    </MModal>
  );
};

export default ChangePassword;
